import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, Tags, Plus, Trash2, X, type LucideIcon } from 'lucide-react';
import { apiService, PackGroupDTO, PackType } from '@/services/api.service';
import { useConfigGroupStore } from '@/stores/configGroup.store';
import { useUnsavedStore } from '@/stores/unsavedDrafts.store';
import { useGameStore } from '@/stores/game.store';
import { usePacksBranchStore } from '@/stores/packsBranch.store';
import { projectMatches } from '@/config/games';
import { GroupPicker } from './GroupPicker';
import { PackThumbnail } from './PackThumbnail';
import { getCachedList, setCachedList, listCacheKey, clearConfigListCache } from './configListCache';
import styles from './ConfigCatalog.module.scss';

/** Groupe du contenu de base du jeu (Vanilla) : décoché par défaut, il se coche à la main. */
function isVanillaGroup(g: PackGroupDTO): boolean {
  return g.type?.toLowerCase() === 'vanilla' || /vanilla/i.test(g.id) || /vanilla/i.test(g.name ?? '');
}

const PAGE_SIZE = 35;

interface ConfigCatalogProps<T> {
  title: string;
  icon: LucideIcon;
  /** Kind pour les types custom (block/item/entity/craft). */
  kind: string;
  /** Charge les entrées pour un groupe donné. */
  load: (groupId: string) => Promise<T[]>;
  keyOf: (item: T) => string;
  identifierOf: (item: T) => string;
  subtitleOf?: (item: T) => string;
  /** Chemin png (relatif au groupe) pour la vignette de liste, ou null. */
  thumbnailOf?: (item: T) => string | null;
  statusOf?: (item: T) => ReactNode;
  /** Chemin du fichier source (relatif au groupe) pour permettre la suppression, ou null si non supprimable. */
  sourceOf?: (item: T) => string | null | undefined;
  matches: (item: T, query: string) => boolean;
  /**
   * Panneau détail (avec preview 3D éventuel) pour une entrée. reload() recharge la liste.
   * `visible` = false quand l'éditeur est monté mais masqué (brouillon non enregistré d'une
   * autre entrée) : les pages doivent alors éviter de monter l'aperçu 3D (WebGL) coûteux.
   */
  renderDetail: (item: T, groupId: string, reload: () => void, group?: PackGroupDTO, visible?: boolean) => ReactNode;
  /**
   * Actions supplémentaires dans l'en-tête (ex. conversion groupée des gros blocs). `reload(groupId)`
   * vide le cache de liste du groupe puis recharge.
   */
  headerExtra?: (ctx: { groups: PackGroupDTO[]; selectedGroupIds: string[]; reload: (groupId: string) => void }) => ReactNode;
}

interface Tagged<T> {
  item: T;
  groupId: string;
}

export function ConfigCatalog<T>({
  title,
  icon: Icon,
  kind,
  load,
  keyOf,
  identifierOf,
  thumbnailOf,
  statusOf,
  sourceOf,
  matches,
  renderDetail,
  headerExtra,
}: ConfigCatalogProps<T>) {
  const { selectedGroupIds, setSelectedGroupIds } = useConfigGroupStore();
  /** Groupes deja vus dans cette session : sert a reperer ceux qui viennent d'apparaitre. */
  const knownGroupIdsRef = useRef<string[]>([]);
  const unsavedDrafts = useUnsavedStore((s) => s.drafts);
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [groups, setGroups] = useState<PackGroupDTO[]>([]);
  // Fichiers modifiés (édités, non publiés) par groupe : groupId -> set de chemins group-relatifs.
  const [editedByGroup, setEditedByGroup] = useState<Record<string, Set<string>>>({});
  // Types custom (catégories) + assignation identifiant -> typeId.
  const [types, setTypes] = useState<PackType[]>([]);
  const [entryTypes, setEntryTypes] = useState<Record<string, string>>({});
  const [typeFilter, setTypeFilter] = useState<string>('all'); // 'all' | 'none' | typeId
  const [showTypeMgr, setShowTypeMgr] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#8B3FF1');
  // Création d'une entrée (block/item/entity) dans une section.
  const [showCreate, setShowCreate] = useState(false);
  const [newId, setNewId] = useState('');
  const [newGroupId, setNewGroupId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const canCreate = kind === 'block' || kind === 'item' || kind === 'entity';
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [items, setItems] = useState<Tagged<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Bascule de branche d'un groupe : le contenu sur disque a changé -> refetch forcé
  // (le store a déjà vidé les caches client). Force le contournement du cache via reloadKey.
  const branchVersion = usePacksBranchStore((s) => s.version);
  const branchVersionRef = useRef(branchVersion);
  useEffect(() => {
    if (branchVersion !== branchVersionRef.current) {
      branchVersionRef.current = branchVersion;
      setReloadKey((k) => k + 1);
    }
  }, [branchVersion]);

  const selKey = selectedGroupIds.join(',');

  // Charge les groupes du projet actif. Tout est coché par défaut, sauf le contenu de base du jeu
  // (Vanilla, demande du 21/10 pour NG comme Paladium), qui se coche à la main. Un groupe qui APPARAÎT
  // est coché lui aussi, Vanilla excepté : sinon son contenu restait invisible sans que rien ne
  // l'indique, puisque la sélection ne mentionnait que les groupes connus au premier chargement.
  useEffect(() => {
    apiService.getPacksGroups().then((res) => {
      const list = (res.data?.groups ?? []).filter((g) => projectMatches(g.projects, activeGameId));
      setGroups(list);
      if (list.length === 0) { setSelectedGroupIds([]); return; }

      const known = new Set(knownGroupIdsRef.current);
      const valid = selectedGroupIds.filter((id) => list.some((g) => g.id === id));
      // Nouveaux groupes = ceux que ce composant n'avait jamais vus dans cette session.
      const parDefaut = list.filter((g) => !isVanillaGroup(g)).map((g) => g.id);
      const fresh = parDefaut.filter((id) => !known.has(id) && !valid.includes(id));
      knownGroupIdsRef.current = list.map((g) => g.id);

      const next = valid.length === 0 ? (parDefaut.length > 0 ? parDefaut : list.map((g) => g.id)) : [...valid, ...fresh];
      if (next.length !== selectedGroupIds.length || next.some((id, i) => id !== selectedGroupIds[i])) {
        setSelectedGroupIds(next);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGameId]);

  // Charge les types custom + assignations pour le projet actif et ce kind.
  const reloadTypes = useCallback(() => {
    apiService.getPackTypes(activeGameId, kind).then((r) => setTypes(r.data?.types ?? []));
    apiService.getPackEntryTypes(activeGameId, kind).then((r) => setEntryTypes(r.data ?? {}));
  }, [activeGameId, kind]);
  useEffect(() => { reloadTypes(); setTypeFilter('all'); }, [reloadTypes]);

  const typeById = useMemo(() => new Map(types.map((t) => [t.id as string, t])), [types]);

  // Charge le contenu de tous les groupes sélectionnés (avec cache client) et fusionne.
  const reloadRef = useRef(reloadKey);
  useEffect(() => {
    const force = reloadKey !== reloadRef.current; // un reload (ex. après save) contourne le cache
    reloadRef.current = reloadKey;
    const ids = selKey ? selKey.split(',') : [];
    if (ids.length === 0) {
      setItems([]);
      return;
    }
    let active = true;

    // Affichage instantané si tous les groupes sont en cache (et pas de reload forcé).
    if (!force) {
      const cached = ids.map((id) => getCachedList<T>(listCacheKey(kind, id)));
      if (cached.every((c) => c !== null)) {
        setItems(cached.flatMap((c, i) => (c as T[]).map((item) => ({ item, groupId: ids[i]! }))));
        setLoading(false);
        return () => { active = false; };
      }
    }

    setLoading(true);
    Promise.all(
      ids.map(async (id) => {
        const key = listCacheKey(kind, id);
        let data = force ? null : getCachedList<T>(key);
        if (!data) {
          data = await load(id);
          setCachedList(key, data);
        }
        return data.map((item) => ({ item, groupId: id }));
      }),
    )
      .then((results) => {
        if (active) setItems(results.flat());
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, reloadKey]);

  // Réinitialise la sélection uniquement quand les sections changent (pas au reload).
  useEffect(() => {
    setSelectedKey(null);
    setPage(0);
  }, [selKey]);

  // Le bouton "Confirmer la suppression" ne doit pas rester actif en changeant d'entrée.
  useEffect(() => { setConfirmDelete(false); }, [selectedKey]);

  // Fichiers modifiés localement (édités, non publiés) pour marquer les entrées dans la liste.
  useEffect(() => {
    const ids = selKey ? selKey.split(',') : [];
    if (ids.length === 0) { setEditedByGroup({}); return; }
    let active = true;
    Promise.all(ids.map(async (id) => [id, new Set((await apiService.getPackChanges(id)).data?.files ?? [])] as const))
      .then((pairs) => { if (active) setEditedByGroup(Object.fromEntries(pairs)); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, reloadKey]);

  const isEdited = (t: Tagged<T>) => !!editedByGroup[t.groupId]?.has(identifierOf(t.item));
  // Modifications en cours non encore enregistrées (n'importe quel sous-éditeur : principal/drop/recette).
  const isUnsaved = (t: Tagged<T>) =>
    Object.values(unsavedDrafts).some(
      (d) => d.kind === kind && d.groupId === t.groupId && d.identifier === identifierOf(t.item),
    );

  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const createType = async () => {
    if (!newTypeName.trim()) return;
    await apiService.savePackType({ project: activeGameId, kind, name: newTypeName.trim(), color: newTypeColor });
    setNewTypeName('');
    reloadTypes();
  };
  const removeType = async (id: string) => {
    await apiService.deletePackType(id);
    if (typeFilter === id) setTypeFilter('all');
    reloadTypes();
  };
  const assignType = async (identifier: string, typeId: string) => {
    await apiService.setPackEntryType(activeGameId, kind, identifier, typeId);
    setEntryTypes((m) => {
      const next = { ...m };
      if (typeId) next[identifier] = typeId;
      else delete next[identifier];
      return next;
    });
  };

  const openCreate = () => {
    setNewGroupId(selectedGroupIds[0] || groups[0]?.id || '');
    setNewId('');
    setCreateErr(null);
    setShowCreate(true);
  };
  const doCreate = async () => {
    const id = newId.trim();
    if (!id || !newGroupId) { setCreateErr('Identifiant et section requis'); return; }
    setCreating(true);
    setCreateErr(null);
    const res = await apiService.createPackEntry(newGroupId, kind, id);
    setCreating(false);
    if (res.data?.success) {
      clearConfigListCache(newGroupId);
      if (!selectedGroupIds.includes(newGroupId)) setSelectedGroupIds([...selectedGroupIds, newGroupId]);
      setShowCreate(false);
      setQuery('');
      setReloadKey((k) => k + 1);
      setSelectedKey(`${newGroupId}::${id}`);
    } else {
      setCreateErr(res.data?.message || res.error || 'Échec de la création');
    }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    const src = sourceOf?.(selected.item);
    if (!src) return;
    const gid = selected.groupId;
    const res = await apiService.deletePackFile(gid, src);
    if (res.data?.success) {
      clearConfigListCache(gid);
      setConfirmDelete(false);
      setSelectedKey(null);
      setReloadKey((k) => k + 1);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? items.filter((t) => matches(t.item, q)) : items;
    if (typeFilter !== 'all') {
      list = list.filter((t) => {
        const tid = entryTypes[identifierOf(t.item)];
        return typeFilter === 'none' ? !tid : tid === typeFilter;
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, typeFilter, entryTypes]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageItems = filtered.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);

  const tagKey = (t: Tagged<T>) => `${t.groupId}::${keyOf(t.item)}`;
  const selected = useMemo(
    () => items.find((t) => tagKey(t) === selectedKey) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, selectedKey],
  );

  // Éditeurs à garder montés : l'entrée sélectionnée + toute entrée de ce kind avec des
  // modifications non enregistrées (pour ne pas perdre le brouillon en changeant d'entrée).
  // Seule la sélectionnée est visible ; les autres restent montées mais masquées.
  const itemByTag = useMemo(() => new Map(items.map((t) => [tagKey(t), t])), [items]);
  const mountedItems = useMemo(() => {
    const tags = new Set<string>();
    if (selectedKey && itemByTag.has(selectedKey)) tags.add(selectedKey);
    for (const d of Object.values(unsavedDrafts)) {
      if (d.kind !== kind) continue;
      const tk = `${d.groupId}::${d.identifier}`;
      if (itemByTag.has(tk)) tags.add(tk);
    }
    return [...tags].map((tk) => itemByTag.get(tk)!).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemByTag, selectedKey, unsavedDrafts, kind]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          <GroupPicker groups={groups} />
          {canCreate && (
            <button className={styles.createBtn} onClick={openCreate}>
              <Plus size={15} /> Créer
            </button>
          )}
          {headerExtra?.({
            groups,
            selectedGroupIds,
            reload: (gid: string) => { clearConfigListCache(gid); setReloadKey((k) => k + 1); },
          })}
        </div>

        {showCreate && (
          <div className={styles.createPanel}>
            <input
              className={styles.createInput}
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="Identifiant (ex. paladium:mon_block)"
              onKeyDown={(e) => e.key === 'Enter' && doCreate()}
              autoFocus
            />
            <select className={styles.createSelect} value={newGroupId} onChange={(e) => setNewGroupId(e.target.value)}>
              {groups.length === 0 && <option value="">Aucune section</option>}
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
            </select>
            <button className={styles.mgrBtn} onClick={doCreate} disabled={creating}>
              {creating ? '…' : 'Créer'}
            </button>
            <button className={styles.mgrBtn} onClick={() => setShowCreate(false)}>Annuler</button>
            {createErr && <span className={styles.createErr}>{createErr}</span>}
          </div>
        )}
        <div className={styles.searchRow}>
          <div className={styles.search}>
            <Search size={16} />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={`Rechercher un ${title.toLowerCase().replace(/s$/, '')}…`}
            />
          </div>
          <span className={styles.count}>
            {loading ? 'Chargement…' : `${filtered.length} / ${items.length}`}
          </span>
        </div>

        <div className={styles.typeBar}>
          <div className={styles.typeChips}>
            <button className={`${styles.typeChip} ${typeFilter === 'all' ? styles.typeChipActive : ''}`} onClick={() => { setTypeFilter('all'); setPage(0); }}>
              Tous
            </button>
            {types.map((t) => (
              <button
                key={t.id}
                className={`${styles.typeChip} ${typeFilter === t.id ? styles.typeChipActive : ''}`}
                style={typeFilter === t.id ? { borderColor: t.color, backgroundColor: `${t.color}22`, color: t.color } : undefined}
                onClick={() => { setTypeFilter(t.id as string); setPage(0); }}
              >
                <span className={styles.typeDot} style={{ backgroundColor: t.color }} />
                {t.name}
              </button>
            ))}
            <button className={`${styles.typeChip} ${typeFilter === 'none' ? styles.typeChipActive : ''}`} onClick={() => { setTypeFilter('none'); setPage(0); }}>
              Sans type
            </button>
          </div>
          <button className={styles.mgrBtn} onClick={() => setShowTypeMgr((v) => !v)}>
            {showTypeMgr ? <X size={14} /> : <Tags size={14} />} Gérer les types
          </button>
        </div>

        {showTypeMgr && (
          <div className={styles.typeMgr}>
            <div className={styles.typeMgrList}>
              {types.length === 0 ? (
                <span className={styles.typeMgrEmpty}>Aucun type pour ce {title.toLowerCase().replace(/s$/, '')}. Crées-en un.</span>
              ) : (
                types.map((t) => (
                  <span key={t.id} className={styles.typeMgrItem} style={{ borderColor: t.color, color: t.color }}>
                    <span className={styles.typeDot} style={{ backgroundColor: t.color }} />
                    {t.name}
                    <button className={styles.typeDel} onClick={() => removeType(t.id as string)} title="Supprimer">
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className={styles.typeMgrForm}>
              <input type="color" value={newTypeColor} onChange={(e) => setNewTypeColor(e.target.value)} />
              <input
                className={styles.typeMgrInput}
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Nom du type…"
                onKeyDown={(e) => e.key === 'Enter' && createType()}
              />
              <button className={styles.mgrBtn} onClick={createType}><Plus size={14} /> Ajouter</button>
            </div>
          </div>
        )}
      </header>

      {selectedGroupIds.length === 0 ? (
        <div className={styles.empty}>
          <Icon size={48} strokeWidth={1.5} />
          <p>Sélectionne au moins une section.</p>
        </div>
      ) : loading ? (
        <div className={styles.empty}>
          <div className={styles.spinner} />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <Icon size={48} strokeWidth={1.5} />
          <h3>Aucun {title.toLowerCase()}</h3>
          <p>Synchronise le(s) groupe(s) (Vue d'ensemble) pour cloner les packs.</p>
        </div>
      ) : (
        <div className={styles.split}>
          <div className={styles.listCol}>
            <div className={styles.list}>
              {pageItems.map((t) => {
                const key = tagKey(t);
                const group = groupById.get(t.groupId);
                const unsaved = isUnsaved(t);
                const edited = !unsaved && isEdited(t);
                return (
                  <button
                    key={key}
                    className={`${styles.row} ${key === selectedKey ? styles.rowActive : ''} ${unsaved ? styles.rowUnsaved : edited ? styles.rowEdited : ''}`}
                    onClick={() => { setSelectedKey(key); setConfirmDelete(false); }}
                    title={unsaved ? 'Modifications non enregistrées' : edited ? 'Modifié — non publié' : undefined}
                  >
                    {thumbnailOf ? (
                      <PackThumbnail groupId={t.groupId} path={thumbnailOf(t.item)} size={36} />
                    ) : (
                      <div className={styles.rowIcon}>
                        <Icon size={18} />
                      </div>
                    )}
                    <div className={styles.rowText}>
                      <span className={styles.rowId}>{identifierOf(t.item)}</span>
                      <span className={styles.rowSub}>
                        {group && (
                          <span className={styles.groupBadge} style={{ borderColor: group.color || '#8B3FF1', color: group.color || '#8B3FF1' }}>
                            {group.name}
                          </span>
                        )}
                        {(() => {
                          const ty = typeById.get(entryTypes[identifierOf(t.item)] ?? '');
                          return ty ? (
                            <span className={styles.typeBadge} style={{ borderColor: ty.color, color: ty.color, backgroundColor: `${ty.color}18` }}>
                              {ty.name}
                            </span>
                          ) : null;
                        })()}
                        {unsaved ? <span className={styles.unsavedBadge}>non enregistré</span> : edited ? <span className={styles.editedBadge}>modifié</span> : null}
                      </span>
                    </div>
                    {statusOf && <div className={styles.rowStatus}>{statusOf(t.item)}</div>}
                  </button>
                );
              })}
            </div>

            {pageCount > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={clampedPage === 0}
                  onClick={() => setPage(clampedPage - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageInfo}>
                  Page {clampedPage + 1} / {pageCount}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={clampedPage >= pageCount - 1}
                  onClick={() => setPage(clampedPage + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>

          <div className={styles.detail}>
            {mountedItems.map((t) => {
              const tk = tagKey(t);
              const visible = tk === selectedKey;
              return (
                <div key={tk} style={visible ? undefined : { display: 'none' }}>
                  {visible && (
                    <div className={styles.typeAssign}>
                      <span className={styles.typeAssignLabel}>Type</span>
                      <select
                        className={styles.typeAssignSelect}
                        value={entryTypes[identifierOf(t.item)] || ''}
                        onChange={(e) => assignType(identifierOf(t.item), e.target.value)}
                      >
                        <option value="">Aucun</option>
                        {types.map((ty) => <option key={ty.id} value={ty.id}>{ty.name}</option>)}
                      </select>
                      {types.length === 0 && <span className={styles.typeAssignHint}>Crée des types via « Gérer les types »</span>}
                      {sourceOf?.(t.item) && (
                        <span className={styles.deleteWrap}>
                          {confirmDelete ? (
                            <>
                              <button className={styles.deleteConfirm} onClick={deleteSelected}>Confirmer</button>
                              <button className={styles.deleteCancel} onClick={() => setConfirmDelete(false)}>Annuler</button>
                            </>
                          ) : (
                            <button className={styles.deleteBtn} onClick={() => setConfirmDelete(true)}><Trash2 size={14} /> Supprimer</button>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                  {renderDetail(t.item, t.groupId, () => setReloadKey((k) => k + 1), groupById.get(t.groupId), visible)}
                </div>
              );
            })}
            {!selected && (
              <div className={styles.detailEmpty}>
                <Icon size={40} strokeWidth={1.5} />
                <p>Sélectionne une entrée pour voir le détail{thumbnailOf ? ' et l’aperçu 3D' : ''}.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
